import _ from "lodash";
import React, { useState, useEffect } from "react";
import { mutate } from "swr";
import cx from "classnames";
import ReactResizeDetector from "react-resize-detector";
import PlaylistView from "./PlaylistView";
import AddPlaylistModal from "../common/AddPlaylistModal";
import { makeRequest } from "../../utils/request";

const Playlists = ({ playlists }) => {
  const [showModal, setShowModal] = useState(false);
  const [show, setShow] = useState(true);
  const [activeDropZone, setActiveDropzone] = useState(null);
  const defaultPlaylist = _.find(playlists, { isDefault: true }) || {
    id: null,
    contentKeys: [],
  };
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
  const [selectedContentKeys, setSelectedContentKeys] = useState(null);
  const [domHeight, setDomHeight] = useState(0);

  useEffect(() => {
    if (selectedPlaylistId === null) {
      if (defaultPlaylist.id === null) {
        if (playlists || playlists.length) {
          handleClickedPlaylist(playlists[0]);
        }
      } else {
        handleClickedPlaylist(defaultPlaylist);
      }
    }
  }, [selectedPlaylistId, defaultPlaylist, playlists]);

  const onResize = (width, height) => {
    setDomHeight(height);
  };

  const handleClickedPlaylist = async (playlist) => {
    setSelectedPlaylistId(playlist.id);
    setSelectedContentKeys(playlist.contentKeys);
  };

  const selectedPlaylist = () => {
    return _.find(playlists, { id: selectedPlaylistId });
  };

  const truncateTab = (title) => {
    return _.truncate(title, {
      length: 15,
      separator: " ",
    });
  };

  const drop = async (event, targetPlaylist) => {
    const { sourcePlaylist, contentKey } = JSON.parse(
      event.dataTransfer.getData("source_data")
    );

    // Block drag & drop on the same playlist
    if (sourcePlaylist.id === targetPlaylist.id) return;

    const removed = deleteContentItem(
      contentKey,
      sourcePlaylist,
      targetPlaylist
    );
    const added = addContentItem(contentKey, targetPlaylist);

    let rmPlaylist;
    if (removed) {
      rmPlaylist = await makeRequest("post", "/api/playlist", removed);
    }
    let adPlaylist;

    if (added) {
      adPlaylist = await makeRequest("post", "/api/playlist", added);
    }
    const newPlaylists = JSON.parse(JSON.stringify(playlists));
    if (removed) {
      newPlaylists[
        newPlaylists.findIndex((x) => x.id === rmPlaylist.id)
      ] = rmPlaylist;
    }
    if (added) {
      newPlaylists[
        newPlaylists.findIndex((x) => x.id === adPlaylist.id)
      ] = adPlaylist;
    }
    mutate("/api/playlist/getallplaylists", newPlaylists, false);

    setActiveDropzone(null);
  };

  const handleDeletePlaylist = async (playlistToDelete) => {
    // Delete playlist
    handleClickedPlaylist(defaultPlaylist);

    await makeRequest("post", "/api/playlist/delete?id=" + playlistToDelete.id);
    mutate(
      "/api/playlist/getallplaylists",
      [
        ...playlists.filter((p) => {
          return p.id.localeCompare(playlistToDelete.id) !== 0;
        }),
      ],
      false
    );
  };

  const deleteContentItem = (contentKey, source, target) => {
    // Validate content item doesn't exist in target playlist
    if (target.contentKeys.indexOf(contentKey) === -1) {
      const contentKeys = source.contentKeys.filter((x) => x !== contentKey);

      let playlist = {
        ...source,
        contentKeys,
      };

      return playlist;
    }

    return null;
  };

  const addContentItem = (contentKey, target) => {
    const contentKeys = [...target.contentKeys];

    // Validate content item doesn't exist in target playlist
    if (contentKeys.indexOf(contentKey) === -1) {
      contentKeys.push(contentKey);
      let playlist = {
        ...target,
        contentKeys,
      };

      return playlist;
    }

    return null;
  };

  return (
    <div className={!show ? "mb-3" : ""}>
      {showModal && (
        <AddPlaylistModal
          closeModal={() => setShowModal(false)}
          playlists={playlists}
        />
      )}
      <div
        className="flex justify-between cursor-pointer"
        onClick={() => setShow(!show)}
      >
        <h2 className="text-2xl ml-5 text-gray-800 font-normal title-font">
          Playlists
        </h2>

        <div>
          <i
            className="fas fa-chevron-down"
            style={{
              transform: !show ? "rotate(-90deg)" : "",
              transition: "all .3s ease",
            }}
          />
        </div>
      </div>

      <div
        style={{
          maxHeight: show ? domHeight + "px" : "0",
          transition: "all .2s ease-out",
        }}
        className={cx({ "overflow-hidden": !show })}
      >
        <div className="flex flex-col sm:flex-row rounded border border-gray-200 mt-3 mb-10">
          <div className="border-gray-200 border-b sm:border-r sm:border-b-0">
            <div className="p-tabs-container justify-start flex sm:flex-col">
              <button
                className="p-tab"
                onClick={(e) => {
                  e.preventDefault();
                  setShowModal(!showModal);
                }}
              >
                <span className="hidden sm:inline mr-2">Add Playlist</span>
                <i className="far fa-plus-square text-lg"></i>
              </button>
              {playlists &&
                playlists.map((item) => (
                  <button
                    onDrop={(e) => {
                      e.preventDefault();
                      drop(e, item);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setActiveDropzone(item.id);
                    }}
                    onDragEnter={() => setActiveDropzone(item.id)}
                    onDragLeave={() => setActiveDropzone(null)}
                    key={item.id}
                    className={`p-tab ${
                      selectedPlaylistId === item.id ||
                      activeDropZone === item.id
                        ? "active"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleClickedPlaylist(item);
                    }}
                  >
                    <span className="align-middle">
                      {truncateTab(item.title)}
                    </span>
                  </button>
                ))}
            </div>
          </div>
          {selectedPlaylist() && (
            <div className="w-full p-4 overflow-auto">
              <PlaylistView
                deletePlaylist={handleDeletePlaylist}
                playlist={selectedPlaylist()}
                playlists={playlists}
                selectedContentKeys={selectedContentKeys}
              />
            </div>
          )}

          <ReactResizeDetector handleWidth handleHeight onResize={onResize} />
        </div>
      </div>
    </div>
  );
};

export default Playlists;
